import React, { useEffect, useState } from "react"
import { cloneOrder, getDateStringFromUTCString, prepareOrderItems, preparePaymentItems, Schema } from "../utils";
import { useCore } from "../hooks";
import { useNavigate, useParams } from "react-router";
import { Customer, Order, OrderItem } from "depoto-core";
import { Btn, Loader, ModalNotesOnExistingOrder, ModalPaymentsOnExistingOrder, OrderFinalSummary } from "../components";
import {printDocument} from "../utils/print.ts";
import { useGQL } from "../utils/GQLContext.ts";
import { toast } from "react-toastify";
import { CheckoutOrderItem } from "../store/core/reducer.ts";

export const OrderDetail: React.FC = () => {
  const params = useParams()
  const navigate = useNavigate()
  const gql = useGQL()
  const { core, currentOrder, setCurrentOrder } = useCore()
  const [order, setOrder] = useState<Order>()
  const [isReturning, setIsReturning] = useState<boolean>(false)

  useEffect(() => {
    if (params?.id) {
      getOrder(Number(params.id))
    }
  }, [params])

  const getOrder = async (orderId: number) => {
    const o = await core.services.order.getById(orderId, Schema.order.detail)
    setOrder(o)
  }

  const backToList = () => {
    navigate('/checkout-detail/orders')
  }

  const showPdf = (url?: string) => {
    core.services.download.downloadDocumentAndShowInNewWindow(url || order.invoiceReceiptUrl, false)
  }

  const printPdf = () => {
    if (window._depoto) {
      printDocument(order.invoiceReceiptUrl, false, true, core)
    } else {
      showPdf()
    }
  }

  const assignCustomer = async (customer: Customer | null) => {
    await core.services.order.updateOrderPart({
      id: order.id,
      customer: customer ? customer : null,
    })
    getOrder(Number(params.id))
  }

  const repeatOrder = () => {
    cloneOrder({ orderToClone: order, currentOrder, setCurrentOrder })
    navigate('/dashboard')
  }

  // sent via gql, core does not send negative price (redeemed voucher), voucher on items and rounding
  const returnOrder = async () => {
    setIsReturning(true)
    try {
      // voucher is fetched (Schema), but core OrderItem type does not know it
      const items: CheckoutOrderItem[] = order.items.map((oi: OrderItem & { voucher?: { id: number } }) => {
        const item: CheckoutOrderItem = { ...oi, id: null, quantity: oi.quantity * -1, isForSubsequentSettlement: oi.isForSubsequentSettlement || false }
        item.voucherId = oi.voucher?.id
        return item
      })
      // voucher on payment would count as another redemption
      const paymentItems = order.paymentItems.map(pi => ({ ...pi, amount: pi.amount * -1, voucher: undefined }))
      const res = await gql('mutation')({
        createOrder: [
          {
            checkout: Number(order.checkout?.id),
            status: 'bill',
            note: `Vratka k objednávce číslo ${order.billNumber}`,
            privateNote: order.privateNote,
            dateCreated: getDateStringFromUTCString(new Date().toUTCString()),
            items: prepareOrderItems({ items }),
            paymentItems: preparePaymentItems({ paymentItems }),
            customer: order.customer?.id > 0 ? order.customer.id : null,
            shippingAddress: order.shippingAddress?.id > 0 ? order.shippingAddress.id : null,
            tags: order.tags?.map(t => t.id),
            currency: order.currency?.id,
            carrier: order.carrier?.id,
            priority: 10,
            relatedParent: order.id,
            rounding: (order.rounding || 0) * -1,
          },
          { data: { id: true }, errors: true },
        ],
      })
      if (!res.createOrder.data?.id || res.createOrder.errors?.length > 0) {
        throw new Error(res.createOrder.errors?.join('\n') || 'Chyba při vytváření vratky')
      }
      const resReturn = await core.services.order.getById(res.createOrder.data.id, Schema.order.detail)
      toast.success('Vratka vytvořena')
      if (window._depoto) {
        printDocument(resReturn.invoiceReceiptUrl, false, true, core)
      } else {
        showPdf(resReturn.invoiceReceiptUrl)
      }
    } catch (e) {
      toast.error(`Chyba při vytváření vratky: ${e instanceof Error ? e.message : e}`)
    } finally {
      setIsReturning(false)
    }
  }

  return order ? (
    <div className="h-body p-6 flex flex-col overflow-y-auto">
      <div className="bg-white border-b-1 border-solid border-border-light rounded-md p-6 mb-6">
        <div className="flex flex-row items-center">
          <h1 className="font-bold mr-4 text-xl">Daňový doklad: {order?.billNumber}</h1>
          <Btn className="btn-base-100" children={'Zpět na výpis'} icon={'fa-arrow-left'} onClick={backToList} />
        </div>
        <div className="flex flex-row gap-2 mt-4">
          <ModalNotesOnExistingOrder order={order} onUpdate={() => getOrder(Number(params.id))} />
          <ModalPaymentsOnExistingOrder order={order} onUpdate={() => getOrder(Number(params.id))} />
          <Btn className="btn-base-100" children={'Pdf účtenka'} icon={'far fa-file-pdf'} onClick={() => showPdf()} />
          <Btn className="btn-base-100" children={'Zrušit (celé dobropisovat)'} icon={'far fa-arrow-rotate-left'} isDisabled={isReturning} onClick={returnOrder} />
          <Btn className="btn-base-100" children={'Znovu objednat'} icon={'far fa-arrow-rotate-right'} onClick={repeatOrder} />
          <Btn className="btn-primary" children={'Vytisknout účtenku'} icon={'far fa-print'} onClick={printPdf} />
        </div>
      </div>
      <div className="w-1/2">
        {<OrderFinalSummary order={order} isReservation={false} showCustomerHeader={false} onExistingOrderCustomerChange={assignCustomer} />}
      </div>
    </div>
  ) : <Loader />
}
